import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';

export type ToastType = 'info' | 'success' | 'error' | 'warning';
export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  createdAt: number;
  timeoutMs: number;
  count?: number; // Anzahl zusammengefasster identischer Toasts
}

export interface ToastContextValue {
  showToast: (type: ToastType, message: string, timeoutMs?: number) => void;
  info: (message: string, timeoutMs?: number) => void;
  success: (message: string, timeoutMs?: number) => void;
  error: (message: string, timeoutMs?: number) => void;
  warning: (message: string, timeoutMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const STORAGE_KEY = 'app.toasts';
  const MERGE_WINDOW_MS = 1500;
  const MAX_PERSIST = 10;

  // Wiederherstellen aus SessionStorage
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const arr: Toast[] = JSON.parse(raw);
      const now = Date.now();
      const restored: Toast[] = [];
      for (const t of arr) {
        const remaining = t.timeoutMs - (now - t.createdAt);
        if (remaining > 300) { // nur wenn noch Zeit übrig ist
          restored.push({ ...t, timeoutMs: remaining, createdAt: now, id: `${now}-${++counter.current}` });
        }
      }
      if (restored.length) setToasts(restored.slice(0, MAX_PERSIST));
    } catch {}
  }, []);

  // Persistieren
  useEffect(() => {
    try {
      const serializable = toasts.map(t => ({ ...t, count: t.count }));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(serializable.slice(-MAX_PERSIST)));
    } catch {}
  }, [toasts]);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((type: ToastType, message: string, timeoutMs = 5000) => {
    const now = Date.now();
    setToasts(prev => {
      // Duplikat innerhalb MERGE_WINDOW_MS suchen
      for (let i = prev.length - 1; i >= 0; i--) {
        const existing = prev[i];
        if (existing.type === type && existing.message === message && (now - existing.createdAt) < MERGE_WINDOW_MS) {
          const updated: Toast = { ...existing, count: (existing.count || 1) + 1, createdAt: now, timeoutMs }; // Timeout neu setzen
          const next = [...prev];
          next[i] = updated;
          return next;
        }
      }
      counter.current += 1;
      const id = `${now}-${counter.current}`;
      const toast: Toast = { id, type, message, createdAt: now, timeoutMs, count: 1 };
      // Removal Timer
      if (timeoutMs > 0) {
        setTimeout(() => remove(id), timeoutMs);
      }
      return [...prev, toast];
    });
  }, [remove]);

  const value = useMemo<ToastContextValue>(() => ({
    showToast,
    info: (m, ms) => showToast('info', m, ms),
    success: (m, ms) => showToast('success', m, ms),
    error: (m, ms) => showToast('error', m, ms),
    warning: (m, ms) => showToast('warning', m, ms),
  }), [showToast]);

  // Accessibility: Announce last toast via aria-live
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!liveRegionRef.current) return;
    const last = toasts[toasts.length - 1];
    if (last) liveRegionRef.current.textContent = last.message;
  }, [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <div className="fixed inset-0 pointer-events-none z-50 flex flex-col items-end p-4 gap-2">
          <div ref={liveRegionRef} aria-live="polite" className="sr-only" />
          {toasts.map(t => (
            <div
              key={t.id}
              // role zuvor "status" belassen für Screenreader; Warnung ignorieren
              role="status"
              className={
                'pointer-events-auto w-80 shadow rounded-md p-3 border text-sm animate-fade-in ' +
                (t.type === 'success' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
                 t.type === 'error' ? 'bg-red-50 border-red-300 text-red-800' :
                 t.type === 'warning' ? 'bg-amber-50 border-amber-300 text-amber-800' :
                 'bg-slate-50 border-slate-300 text-slate-800')
              }
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 whitespace-pre-wrap">
                  {t.message}{t.count && t.count > 1 && <span className="ml-2 text-xs font-medium text-slate-500">×{t.count}</span>}
                </div>
                <button
                  onClick={() => remove(t.id)}
                  className="text-slate-500 hover:text-slate-700 focus:outline-none"
                  aria-label="Toast schließen"
                >×</button>
              </div>
              <div className="mt-2 h-1 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-full bg-[var(--primary-600)]"
                  style={{
                    width: '100%',
                    animation: `toast-progress linear forwards`,
                    animationDuration: `${t.timeoutMs}ms`
                  }}
                />
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
      <style>
        {`
        @keyframes toast-progress { from { width:100% } to { width:0% } }
        @keyframes fade-in { from { opacity:0; transform:translateY(4px) } to { opacity:1; transform:translateY(0) } }
        .animate-fade-in { animation: fade-in .15s ease-out; }
        `}
      </style>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast muss innerhalb ToastProvider verwendet werden');
  return ctx;
}
